import React, { useState, useEffect } from 'react';
import {
  Sparkles, Loader, Copy, Check, Lock, Unlock,
  Clock, Zap, ArrowLeft, Link as LinkIcon, FlaskConical,
  Star, Share2, X
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import ModelLogo from './ModelLogo';
import { showToast } from './Toast';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const isRTL = (text) => {
  if (!text) return false;
  return /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/.test(text.substring(0, 100));
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diffMs = new Date() - new Date(dateStr);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const TONE_BORDER_TOP = {
  professional: 'border-t-blue-500', friendly: 'border-t-emerald-500',
  angry: 'border-t-red-500', funny: 'border-t-amber-500',
  philosophical: 'border-t-purple-500', dramatic: 'border-t-orange-500',
};
const TONE_TEXT = {
  professional: 'text-blue-600', friendly: 'text-emerald-600',
  angry: 'text-red-600', funny: 'text-amber-600',
  philosophical: 'text-purple-600', dramatic: 'text-orange-600',
};

function ToneResultCard({ result, index }) {
  const [copied, setCopied] = useState(false);
  const borderTop = TONE_BORDER_TOP[result.tone_key] || 'border-t-gray-400';
  const textColor = TONE_TEXT[result.tone_key] || 'text-gray-600';
  const rtl = isRTL(result.output);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(result.output);
    setCopied(true);
    showToast('Copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15, duration: 0.4 }}
      className={`bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden border-t-4 ${borderTop} flex flex-col`}
    >
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{result.tone_emoji}</span>
          <span className={`font-bold text-sm ${textColor}`}>{result.tone_label}</span>
        </div>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
            copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
          }`}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="px-4 pb-3 flex-1">
        <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap" dir={rtl ? 'rtl' : 'ltr'} style={{ textAlign: rtl ? 'right' : 'left' }}>
          {result.output}
        </p>
      </div>
      <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400 mt-auto">
        {result.latency_ms > 0 && (
          <span className="flex items-center gap-1"><Clock size={12} />{(result.latency_ms / 1000).toFixed(1)}s</span>
        )}
        {result.tokens > 0 && (
          <span className="flex items-center gap-1"><Zap size={12} />{result.tokens} tokens</span>
        )}
      </div>
    </motion.div>
  );
}

function ToneLabDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = React.useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const loadRun = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // STATIC MODE: load from local JSON file and find by id
        const res = await fetch('/data/tonelab.json');
        if (!res.ok) throw new Error('Failed to load');
        const allData = await res.json();
        const data = (allData.runs || []).find(r => r.id === id);
        if (!data) throw new Error('Run not found');
        setRun(data);
        setIsPublic(data.is_public);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    };
    if (id) loadRun();
  }, [id, token]);

  const handleTogglePublic = async () => {
    if (!run?.id) return;
    try {
      const res = await fetch(`${API_URL}/tonelab/view/${run.id}/toggle-public`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsPublic(data.is_public);
        showToast(data.is_public ? '🌐 Made public!' : '🔒 Made private', 'success');
      }
    } catch (e) { showToast('Failed to update visibility', 'error'); }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    showToast('Link copied!', 'success');
  };

  const handleVote = async () => {
    if (!token) {
      showToast('Please login to vote', 'warning');
      return;
    }
    if (votingInProgress) return;
    setVotingInProgress(true);
    const wasVoted = run.user_voted;
    setRun(prev => ({
      ...prev,
      votes: wasVoted ? (prev.votes || 1) - 1 : (prev.votes || 0) + 1,
      user_voted: !wasVoted
    }));
    try {
      const res = await fetch(`${API_URL}/tonelab/view/${run.id}/vote`, {
        method: wasVoted ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        setRun(prev => ({ ...prev, votes: wasVoted ? (prev.votes || 0) + 1 : (prev.votes || 1) - 1, user_voted: wasVoted }));
      }
    } catch {
      setRun(prev => ({ ...prev, votes: wasVoted ? (prev.votes || 0) + 1 : (prev.votes || 1) - 1, user_voted: wasVoted }));
    } finally { setVotingInProgress(false); }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `AI Tone: ${run?.original_text?.slice(0, 80) || ''}`;

  const shareToWhatsApp = () => { window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank'); setShowShareMenu(false); };
  const shareToTwitter = () => { window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank'); setShowShareMenu(false); };
  const shareToTelegram = () => { window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank'); setShowShareMenu(false); };
  const shareCopyLink = () => { navigator.clipboard.writeText(shareUrl); showToast('Link copied!', 'success'); setShowShareMenu(false); };

  // Close share menu on outside click
  React.useEffect(() => {
    const handler = (e) => { if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) setShowShareMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12 flex items-center justify-center"><Loader className="animate-spin text-rose-500" size={32} /></div>;
  if (error) return (
    <div className="max-w-4xl mx-auto px-4 py-12 text-center">
      <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl inline-block">
        <p className="font-medium">{error}</p>
        <button onClick={() => navigate('/tonelab')} className="text-rose-500 text-sm mt-2 hover:underline">← Back to AI Tone</button>
      </div>
    </div>
  );
  if (!run) return null;

  const textRtl = isRTL(run.original_text);

  return (
    <div className="px-4 sm:px-0 py-6">
    <div className="max-w-4xl mx-auto px-0 sm:px-6 space-y-6">
      <button onClick={() => navigate('/tonelab')} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors">
        <ArrowLeft size={18} /> Back to AI Tone
      </button>

      {/* Run Header - Clean white card with rose accent */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="h-1 bg-gradient-to-r from-rose-400 to-pink-500 rounded-t-xl" />
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3 text-gray-500 text-sm">
            <FlaskConical size={16} className="text-rose-500" />
            <span className="font-medium text-gray-900">AI Tone</span>
            <span>•</span>
            <span>{formatTimeAgo(run.created_at)}</span>
            {run.user && <><span>•</span><span>by {run.user}</span></>}
          </div>

          <h1
            className="text-lg sm:text-xl font-bold text-gray-900 leading-relaxed mb-4"
            dir={textRtl ? 'rtl' : 'ltr'}
            style={{ textAlign: textRtl ? 'right' : 'left' }}
          >
            {run.original_text}
          </h1>

          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {run.model_name && (
              <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-2.5 py-1">
                <ModelLogo modelName={run.model_name} size="xs" />
                <span className="text-sm text-gray-700">{run.model_name}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-2.5 py-1 text-sm text-gray-600">
              <Sparkles size={14} className="text-rose-500" />
              {run.results?.length || 0} tones
            </div>
            {run.total_tokens > 0 && (
              <div className="flex items-center gap-1.5 bg-gray-100 rounded-full px-2.5 py-1 text-sm text-gray-600">
                <Zap size={14} />
                {run.total_tokens} tokens
              </div>
            )}
          </div>

          {/* Action buttons - matching ChatDetail */}
          <div className="flex flex-wrap items-center gap-2 pt-4 mt-4 border-t border-gray-100">
            {/* Vote button */}
            <button
              onClick={handleVote}
              disabled={votingInProgress}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all ${
                run.user_voted
                  ? 'bg-green-100 text-green-700 font-bold shadow-sm'
                  : (run.votes || 0) > 0
                    ? 'bg-green-50 text-green-600'
                    : 'bg-gray-100 text-gray-500 hover:text-green-500 hover:bg-green-50'
              } ${votingInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={run.user_voted ? 'Remove vote' : 'Vote'}
            >
              <Star size={18} fill={run.user_voted || (run.votes || 0) > 0 ? 'currentColor' : 'none'} />
              {(run.votes || 0) > 0 && (
                <span className="text-sm font-medium">{run.votes}</span>
              )}
            </button>

            {/* Share */}
            <div className="relative" ref={shareMenuRef}>
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors text-blue-700"
              >
                <Share2 size={16} />
                <span className="text-sm font-medium">Share</span>
              </button>

              {showShareMenu && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-semibold text-gray-800 text-sm">Share to</span>
                    <button onClick={() => setShowShareMenu(false)} className="p-1 hover:bg-gray-100 rounded">
                      <X size={14} className="text-gray-500" />
                    </button>
                  </div>
                  <div className="p-2">
                    <button onClick={shareToWhatsApp} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                      <span className="text-lg">💬</span>
                      <span className="text-sm font-medium text-gray-700">WhatsApp</span>
                    </button>
                    <button onClick={shareToTwitter} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                      <span className="text-lg">𝕏</span>
                      <span className="text-sm font-medium text-gray-700">Twitter / X</span>
                    </button>
                    <button onClick={shareToTelegram} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                      <span className="text-lg">✈️</span>
                      <span className="text-sm font-medium text-gray-700">Telegram</span>
                    </button>
                    <button onClick={shareCopyLink} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                      <LinkIcon size={16} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Copy Link</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Public/Private toggle - matching ChatDetail */}
            {run.is_owner && (
              <button
                onClick={handleTogglePublic}
                title={isPublic ? 'Make Private' : 'Make Public'}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {isPublic ? <Unlock size={16} className="text-green-600" /> : <Lock size={16} className="text-gray-500" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tone Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AnimatePresence>
          {run.results?.map((result, i) => (
            <ToneResultCard key={result.tone_key || i} result={result} index={i} />
          ))}
        </AnimatePresence>
      </div>

    </div>
    </div>
  );
}

export default ToneLabDetail;